/**
 * Cache de nomes de item resolvido via /api/items/names (batch lookup em
 * Server01.dbo.cabal_item_list). Items nao mudam — cache eh permanente
 * durante a sessao (sem TTL local; backend tem TTL 1h pra picks novos).
 *
 * Uso:
 *   useItemNames([1, 1698, 2208]) -> { 1: "Nucleo...", 1698: "Ataque Magico", ... }
 *
 * Implementacao:
 *   - Mantem Map<itemId, name|null|"pending"> em memoria
 *   - Ao requisitar IDs nao cacheados, agrupa por 50ms (debounce) e dispara
 *     1 fetch batch pra todos pendentes. Re-render ao chegar resposta.
 */
import { useEffect, useState } from 'react';
import { API_URL } from '@/shared/services/authServices';

type NameValue = string | null | 'pending';
const CACHE = new Map<number, NameValue>();
const SUBSCRIBERS = new Set<() => void>();

let pendingIds: Set<number> = new Set();
let pendingTimer: ReturnType<typeof setTimeout> | null = null;

function notify() {
  for (const s of SUBSCRIBERS) s();
}

async function flush() {
  pendingTimer = null;
  const ids = Array.from(pendingIds);
  pendingIds = new Set();
  if (ids.length === 0) return;

  // marca como "pending" pra evitar disparo duplo do mesmo ID
  for (const i of ids) {
    if (CACHE.get(i) === undefined) CACHE.set(i, 'pending');
  }
  notify();

  try {
    const url = `${API_URL}/items/names?ids=${ids.join(',')}`;
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data: Record<string, string | null> = await res.json();
    for (const i of ids) {
      const name = data[String(i)] ?? null;
      CACHE.set(i, name);
    }
  } catch (err) {
    // marca todos como null em erro pra nao re-tentar em loop
    for (const i of ids) {
      if (CACHE.get(i) === 'pending') CACHE.set(i, null);
    }
    console.warn('[itemNames] batch fetch falhou:', err);
  }
  notify();
}

function request(id: number) {
  if (CACHE.has(id)) return;
  pendingIds.add(id);
  if (!pendingTimer) {
    pendingTimer = setTimeout(flush, 50);
  }
}

/**
 * Hook que resolve uma lista de IDs e re-renderiza ao chegar resposta.
 * Retorna `null` enquanto pendente, string com nome quando resolvido,
 * ou `undefined` se nao tem nome no DB (ID raro/inexistente).
 */
export function useItemNames(ids: number[]): Map<number, string | null> {
  const [, force] = useState(0);

  useEffect(() => {
    for (const id of ids) request(id);
    const sub = () => force(n => n + 1);
    SUBSCRIBERS.add(sub);
    return () => { SUBSCRIBERS.delete(sub); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids.join(',')]);

  const out = new Map<number, string | null>();
  for (const id of ids) {
    const v = CACHE.get(id);
    if (v === undefined || v === 'pending') out.set(id, null);
    else out.set(id, v);
  }
  return out;
}
